import { spawn } from "node:child_process";
import { buildCliLaunchInvocation, classifyRuntimePermissionDenialMessage, } from "@wakeloop/adapter-core";
import { classifyCodexProviderAccountFailure } from "../output/codex-provider-failure.js";
const APP_SERVER_PROCESS_KILL_GRACE_MS = 1000;
export class CodexAppServerTransportError extends Error {
    code;
    errorType;
    constructor(input) {
        super(input.message);
        this.name = "CodexAppServerTransportError";
        this.code = input.code;
        this.errorType = input.errorType;
    }
}
export async function invokeCodexAppServerTransport(input) {
    const prompt = normalizeOptionalText(input.prompt);
    if (!prompt) {
        throw new CodexAppServerTransportError({
            code: "schema.invalid",
            errorType: "config",
            message: "schema.invalid: prompt is required",
        });
    }
    const invokedAtMs = Date.now();
    const permissionModeParams = resolveCodexAppServerPermissionModeParams(input.agentControllerPermissionMode);
    const conversationPolicyParams = resolveCodexAppServerConversationPolicyParams(input.conversationPolicy);
    return await withCodexAppServerSession({
        codexTimeoutMs: input.codexTimeoutMs,
        codexWorkdir: input.codexWorkdir,
        env: input.env,
        launchContract: input.launchContract,
        onProcessControlEvent: input.onProcessControlEvent,
        onRuntimeEvent: input.onRuntimeEvent,
    }, async (session) => {
        const threadId = await resolveDispatchThreadId({
            conversationPolicyParams,
            codexWorkdir: input.codexWorkdir,
            permissionModeParams: permissionModeParams.thread,
            session,
            threadId: input.threadId,
        });
        session.emitProviderConversationObserved(threadId);
        const turnResponse = await session.request("turn/start", {
            ...permissionModeParams.turn,
            cwd: input.codexWorkdir,
            input: [
                {
                    text: prompt,
                    text_elements: [],
                    type: "text",
                },
            ],
            threadId,
        });
        const turn = readTurnStartResponse(turnResponse);
        const turnId = readRequiredText(turn.id, "turn/start response turn.id");
        const unregisterActiveTurn = session.registerActiveTurn({
            onActiveTurn: input.onActiveTurn,
            taskId: input.taskId,
            threadId,
            turnId,
        });
        let completedTurn;
        try {
            completedTurn =
                turn.status === "completed"
                    ? turn
                    : await session.waitForTurnCompleted({
                        threadId,
                        turnId,
                    });
        }
        finally {
            unregisterActiveTurn?.();
        }
        const output = normalizeOptionalText(readAgentMessageOutput(completedTurn) ??
            session.readCollectedAgentMessageDeltaText());
        if (completedTurn.status === "failed") {
            const failureMessage = readTurnFailureMessage(completedTurn);
            const permissionDenial = classifyRuntimePermissionDenialMessage({
                message: failureMessage,
                source: "codex",
            });
            if (permissionDenial) {
                throw new CodexAppServerTransportError({
                    code: permissionDenial.code,
                    errorType: permissionDenial.errorType,
                    message: permissionDenial.message,
                });
            }
            const providerFailure = classifyCodexProviderAccountFailure({
                codexErrorInfo: readTurnFailureCodexErrorInfo(completedTurn),
                message: failureMessage,
            });
            if (providerFailure) {
                throw new CodexAppServerTransportError({
                    code: providerFailure.code,
                    errorType: providerFailure.errorType,
                    message: failureMessage,
                });
            }
            throw new CodexAppServerTransportError({
                code: "runtime.turn.failed",
                errorType: "adapter",
                message: `runtime.turn.failed: ${failureMessage}`,
            });
        }
        if (output) {
            session.markAgentOutputObserved();
        }
        await emitPreviewIfRequested({
            onPreview: input.onPreview,
            output: output ?? "",
            streamingMode: input.streamingMode,
        });
        return {
            output: output ?? "",
            telemetry: session.buildTelemetry({
                invokedAtMs,
                previewEmitted: input.streamingMode === "stream" && input.onPreview !== undefined,
                resumedContext: normalizeOptionalText(input.threadId) !== null,
                streamingMode: input.streamingMode ?? "final",
            }),
            threadId,
        };
    });
}
export async function verifyCodexAppServerThread(input) {
    const threadId = normalizeOptionalText(input.threadId);
    if (!threadId) {
        throw new CodexAppServerTransportError({
            code: "schema.invalid",
            errorType: "config",
            message: "schema.invalid: threadId is required",
        });
    }
    return await withCodexAppServerSession({
        codexTimeoutMs: input.codexTimeoutMs,
        codexWorkdir: input.codexWorkdir,
        env: input.env,
        launchContract: input.launchContract,
    }, async (session) => {
        const response = await session.request("thread/resume", {
            approvalPolicy: "untrusted",
            cwd: input.codexWorkdir,
            threadId,
        });
        return {
            threadId: readThreadIdFromResponse(response),
        };
    });
}
async function resolveDispatchThreadId(input) {
    const existingThreadId = normalizeOptionalText(input.threadId);
    if (existingThreadId) {
        const response = await input.session.request("thread/resume", {
            ...input.conversationPolicyParams,
            ...input.permissionModeParams,
            cwd: input.codexWorkdir,
            threadId: existingThreadId,
        });
        return readThreadIdFromResponse(response);
    }
    const response = await input.session.request("thread/start", {
        ...input.conversationPolicyParams,
        ...input.permissionModeParams,
        cwd: input.codexWorkdir,
        threadSource: "user",
    });
    return readThreadIdFromResponse(response);
}
function resolveCodexAppServerPermissionModeParams(value) {
    const permissionMode = resolveCodexAgentControllerPermissionMode(value);
    switch (permissionMode) {
        case "codex:approveForMe":
            return {
                thread: {
                    approvalPolicy: "on-request",
                    approvalsReviewer: "auto_review",
                    sandbox: "workspace-write",
                },
                turn: {
                    approvalPolicy: "on-request",
                    approvalsReviewer: "auto_review",
                },
            };
        case "codex:fullAccess":
            return {
                thread: {
                    approvalPolicy: "never",
                    sandbox: "danger-full-access",
                },
                turn: {
                    approvalPolicy: "never",
                },
            };
        case "codex:customConfig":
            return {
                thread: {},
                turn: {},
            };
        default:
            return {
                thread: {
                    approvalPolicy: "untrusted",
                    sandbox: "workspace-write",
                },
                turn: {
                    approvalPolicy: "untrusted",
                },
            };
    }
}
function resolveCodexAgentControllerPermissionMode(value) {
    switch (value) {
        case "codex:approveForMe":
        case "codex:fullAccess":
        case "codex:customConfig":
            return value;
        default:
            return "codex:askForApproval";
    }
}
function resolveCodexAppServerConversationPolicyParams(conversationPolicy) {
    const params = {};
    const model = normalizeOptionalText(conversationPolicy?.model);
    if (model) {
        params.model = model;
    }
    const reasoningLevel = normalizeOptionalText(conversationPolicy?.reasoningLevel);
    if (reasoningLevel) {
        params.reasoningEffort = reasoningLevel;
    }
    return params;
}
async function withCodexAppServerSession(input, callback) {
    const session = new CodexAppServerSession(input);
    try {
        await session.start();
        return await callback(session);
    }
    catch (error) {
        throw toCodexAppServerTransportError(error, input.codexTimeoutMs);
    }
    finally {
        await session.close();
    }
}
class CodexAppServerSession {
    #codexTimeoutMs;
    #codexWorkdir;
    #env;
    #launchContract;
    #notifications = [];
    #onProcessControlEvent;
    #onRuntimeEvent;
    #pending = new Map();
    #turnAgentMessageDeltas = new Map();
    #child = null;
    #forceKillTimeout = null;
    #firstOutputAtMs = null;
    #lineBuffer = "";
    #nextRequestId = 1;
    #processTimeout = null;
    #settledFailure = null;
    #waiters = new Set();
    #lifecycle = {
        exitCode: null,
        exitObservedAtMs: null,
        exitSignal: null,
        firstStderrAtMs: null,
        firstStdoutAtMs: null,
        processId: null,
        processSpawnedAtMs: null,
        stdinClosedAtMs: null,
        timeoutTriggeredAtMs: null,
    };
    constructor(input) {
        this.#codexTimeoutMs = input.codexTimeoutMs;
        this.#codexWorkdir = input.codexWorkdir;
        this.#env = input.env;
        this.#launchContract = input.launchContract;
        this.#onProcessControlEvent = input.onProcessControlEvent;
        this.#onRuntimeEvent = input.onRuntimeEvent;
    }
    async start() {
        const invocation = buildCliLaunchInvocation({
            args: ["app-server", "--listen", "stdio://"],
            launchContract: this.#launchContract,
        });
        const child = spawn(invocation.bin, invocation.args, {
            cwd: this.#codexWorkdir,
            env: {
                ...process.env,
                ...(this.#env ?? {}),
            },
            shell: invocation.shell ?? false,
            stdio: ["pipe", "pipe", "pipe"],
        });
        this.#child = child;
        const processId = typeof child.pid === "number" ? child.pid : null;
        const processSpawnedAtMs = Date.now();
        this.#lifecycle.processId = processId;
        this.#lifecycle.processSpawnedAtMs = processSpawnedAtMs;
        this.#emitRuntimeEvent({
            phase: "process_spawned",
            processId,
            tsMs: processSpawnedAtMs,
        });
        this.#onProcessControlEvent?.({
            phase: "process_spawned",
            processId,
        });
        child.stdout.setEncoding("utf8");
        child.stdout.on("data", (chunk) => {
            const text = String(chunk);
            if (this.#lifecycle.firstStdoutAtMs === null) {
                const firstStdoutAtMs = Date.now();
                this.#lifecycle.firstStdoutAtMs = firstStdoutAtMs;
                this.#emitRuntimeEvent({
                    phase: "first_stdout",
                    processId,
                    tsMs: firstStdoutAtMs,
                });
            }
            this.#consumeStdoutText(text);
        });
        child.stderr.setEncoding("utf8");
        child.stderr.on("data", () => {
            if (this.#lifecycle.firstStderrAtMs !== null) {
                return;
            }
            const firstStderrAtMs = Date.now();
            this.#lifecycle.firstStderrAtMs = firstStderrAtMs;
            this.#emitRuntimeEvent({
                phase: "first_stderr",
                processId,
                tsMs: firstStderrAtMs,
            });
        });
        child.on("error", (error) => {
            this.#failAll(new CodexAppServerTransportError({
                code: "upstream_unreachable",
                errorType: "adapter",
                message: `upstream_unreachable: failed to spawn codex app-server (${error.message})`,
            }));
        });
        child.on("close", (exitCode, signal) => {
            this.#clearTimers();
            this.#lifecycle.exitCode = exitCode;
            this.#lifecycle.exitObservedAtMs = Date.now();
            this.#lifecycle.exitSignal = signal;
            this.#emitRuntimeEvent({
                exitCode,
                phase: "process_exit",
                processId,
                signal,
                tsMs: this.#lifecycle.exitObservedAtMs,
            });
            this.#onProcessControlEvent?.({
                phase: "process_exit",
                processId,
            });
            if (this.#pending.size > 0 || this.#waiters.size > 0) {
                this.#failAll(new CodexAppServerTransportError({
                    code: "upstream_unreachable",
                    errorType: "adapter",
                    message: "upstream_unreachable: codex app-server exited early",
                }));
            }
        });
        this.#processTimeout =
            this.#codexTimeoutMs === null
                ? null
                : setTimeout(() => {
                    const timeoutTriggeredAtMs = Date.now();
                    this.#lifecycle.timeoutTriggeredAtMs = timeoutTriggeredAtMs;
                    this.#emitRuntimeEvent({
                        phase: "timeout_triggered",
                        processId,
                        tsMs: timeoutTriggeredAtMs,
                    });
                    this.#failAll(new CodexAppServerTransportError({
                        code: "process.timeout",
                        errorType: "timeout",
                        message: this.#codexTimeoutMs === null
                            ? "process.timeout: codex app-server timed out"
                            : `process.timeout: codex app-server did not complete within ${this.#codexTimeoutMs}ms`,
                    }));
                    this.#requestProcessTermination();
                }, this.#codexTimeoutMs);
        await this.request("initialize", {
            capabilities: {
                experimentalApi: true,
            },
            clientInfo: {
                name: "wakeloop",
                version: "0.0.0",
            },
        });
        this.notify("initialized", {});
    }
    async close() {
        this.#clearTimers();
        const child = this.#child;
        if (!child) {
            return;
        }
        if (this.#lifecycle.stdinClosedAtMs === null) {
            this.#lifecycle.stdinClosedAtMs = Date.now();
            this.#emitRuntimeEvent({
                phase: "stdin_closed",
                processId: this.#lifecycle.processId,
                tsMs: this.#lifecycle.stdinClosedAtMs,
            });
        }
        child.stdin.end();
        if (this.#lifecycle.exitObservedAtMs !== null) {
            return;
        }
        this.#requestProcessTermination();
        await new Promise((resolve) => {
            const timeout = setTimeout(() => {
                resolve();
            }, APP_SERVER_PROCESS_KILL_GRACE_MS);
            child.once("close", () => {
                clearTimeout(timeout);
                resolve();
            });
        });
    }
    async request(method, params) {
        if (this.#settledFailure) {
            throw this.#settledFailure;
        }
        const id = this.#nextRequestId;
        this.#nextRequestId += 1;
        const requestPromise = new Promise((resolve, reject) => {
            this.#pending.set(id, {
                reject,
                resolve,
            });
        });
        this.#write({
            id,
            jsonrpc: "2.0",
            method,
            params,
        });
        return await requestPromise;
    }
    notify(method, params) {
        this.#write({
            jsonrpc: "2.0",
            method,
            ...(params === undefined ? {} : { params }),
        });
    }
    async waitForTurnCompleted(input) {
        const message = await this.#waitForNotification((candidate) => {
            if (candidate.method !== "turn/completed") {
                return false;
            }
            const params = toRecord(candidate.params);
            const turn = toRecord(params?.turn);
            return (params?.threadId === input.threadId &&
                normalizeOptionalText(turn?.id) === input.turnId);
        });
        const params = toRecord(message.params);
        const turn = toRecord(params?.turn);
        if (!turn) {
            throw new CodexAppServerTransportError({
                code: "protocol.invalid",
                errorType: "adapter",
                message: "protocol.invalid: turn/completed did not include turn",
            });
        }
        return turn;
    }
    registerActiveTurn(input) {
        if (!input.onActiveTurn) {
            return null;
        }
        return input.onActiveTurn({
            taskId: input.taskId,
            threadId: input.threadId,
            turnId: input.turnId,
            interrupt: async () => {
                await this.request("turn/interrupt", {
                    threadId: input.threadId,
                    turnId: input.turnId,
                });
            },
        });
    }
    buildTelemetry(input) {
        return {
            completedAtMs: Date.now(),
            ...(this.#lifecycle.exitCode === null
                ? {}
                : { exitCode: this.#lifecycle.exitCode }),
            ...(this.#lifecycle.exitObservedAtMs === null
                ? {}
                : { exitObservedAtMs: this.#lifecycle.exitObservedAtMs }),
            ...(this.#lifecycle.exitSignal === null
                ? {}
                : { exitSignal: this.#lifecycle.exitSignal }),
            ...(this.#firstOutputAtMs === null
                ? {}
                : { firstOutputAtMs: this.#firstOutputAtMs }),
            ...(this.#lifecycle.firstStderrAtMs === null
                ? {}
                : { firstStderrAtMs: this.#lifecycle.firstStderrAtMs }),
            ...(this.#lifecycle.firstStdoutAtMs === null
                ? {}
                : { firstStdoutAtMs: this.#lifecycle.firstStdoutAtMs }),
            invokedAtMs: input.invokedAtMs,
            previewEmitted: input.previewEmitted,
            ...(this.#lifecycle.processId === null
                ? {}
                : { processId: this.#lifecycle.processId }),
            ...(this.#lifecycle.processSpawnedAtMs === null
                ? {}
                : { processSpawnedAtMs: this.#lifecycle.processSpawnedAtMs }),
            resumedContext: input.resumedContext,
            ...(this.#lifecycle.stdinClosedAtMs === null
                ? {}
                : { stdinClosedAtMs: this.#lifecycle.stdinClosedAtMs }),
            streamingMode: input.streamingMode,
            ...(this.#lifecycle.timeoutTriggeredAtMs === null
                ? {}
                : { timeoutTriggeredAtMs: this.#lifecycle.timeoutTriggeredAtMs }),
        };
    }
    readCollectedAgentMessageDeltaText() {
        const output = [...this.#turnAgentMessageDeltas.values()].join("").trim();
        return output.length > 0 ? output : null;
    }
    markAgentOutputObserved() {
        if (this.#firstOutputAtMs !== null) {
            return;
        }
        this.#firstOutputAtMs = Date.now();
    }
    #consumeStdoutText(text) {
        this.#lineBuffer += text;
        while (true) {
            const newlineIndex = this.#lineBuffer.indexOf("\n");
            if (newlineIndex < 0) {
                return;
            }
            const line = this.#lineBuffer.slice(0, newlineIndex).trim();
            this.#lineBuffer = this.#lineBuffer.slice(newlineIndex + 1);
            if (!line) {
                continue;
            }
            this.#handleStdoutLine(line);
        }
    }
    #handleStdoutLine(line) {
        let message;
        try {
            message = JSON.parse(line);
        }
        catch (error) {
            this.#failAll(new CodexAppServerTransportError({
                code: "protocol.invalid",
                errorType: "adapter",
                message: `protocol.invalid: codex app-server emitted non-JSON output (${toErrorMessage(error)})`,
            }));
            return;
        }
        if (message.id !== undefined && message.method) {
            this.#handleServerRequest(message);
            return;
        }
        if (message.id !== undefined) {
            this.#handleResponse(message);
            return;
        }
        if (message.method) {
            this.#handleNotification(message);
        }
    }
    #handleResponse(message) {
        const id = message.id;
        if (!(typeof id === "number" || typeof id === "string")) {
            return;
        }
        const pending = this.#pending.get(id);
        if (!pending) {
            return;
        }
        this.#pending.delete(id);
        if (message.error) {
            pending.reject(toJsonRpcTransportError(message.error));
            return;
        }
        pending.resolve(message.result);
    }
    #handleNotification(message) {
        this.#notifications.push(message);
        this.#collectAgentMessageDelta(message);
        for (const waiter of [...this.#waiters]) {
            if (!waiter.predicate(message)) {
                continue;
            }
            this.#waiters.delete(waiter);
            waiter.resolve(message);
        }
    }
    #handleServerRequest(message) {
        const id = message.id;
        if (!(typeof id === "number" || typeof id === "string")) {
            return;
        }
        const result = buildServerRequestDeclineResponse(message.method);
        if (result === null) {
            this.#write({
                error: {
                    code: -32_601,
                    message: `WakeLoop Codex App Server transport does not handle server request '${message.method}'.`,
                },
                id,
                jsonrpc: "2.0",
            });
            return;
        }
        this.#write({
            id,
            jsonrpc: "2.0",
            result,
        });
    }
    #waitForNotification(predicate) {
        for (const notification of this.#notifications) {
            if (predicate(notification)) {
                return Promise.resolve(notification);
            }
        }
        if (this.#settledFailure) {
            return Promise.reject(this.#settledFailure);
        }
        return new Promise((resolve, reject) => {
            this.#waiters.add({
                predicate,
                reject,
                resolve,
            });
        });
    }
    #collectAgentMessageDelta(message) {
        if (message.method !== "item/agentMessage/delta") {
            return;
        }
        const params = toRecord(message.params);
        const itemId = normalizeOptionalText(params?.itemId);
        const delta = readAgentMessageDeltaText(params?.delta);
        if (!(itemId && delta)) {
            return;
        }
        this.markAgentOutputObserved();
        this.#turnAgentMessageDeltas.set(itemId, `${this.#turnAgentMessageDeltas.get(itemId) ?? ""}${delta}`);
    }
    #write(message) {
        const child = this.#child;
        if (!child) {
            throw new CodexAppServerTransportError({
                code: "upstream_unreachable",
                errorType: "adapter",
                message: "upstream_unreachable: codex app-server is not running",
            });
        }
        child.stdin.write(`${JSON.stringify(message)}\n`);
    }
    #failAll(error) {
        if (!this.#settledFailure) {
            this.#settledFailure = error;
        }
        for (const pending of this.#pending.values()) {
            pending.reject(error);
        }
        this.#pending.clear();
        for (const waiter of this.#waiters) {
            waiter.reject(error);
        }
        this.#waiters.clear();
    }
    #requestProcessTermination() {
        const child = this.#child;
        if (!child || child.killed) {
            return;
        }
        child.kill("SIGTERM");
        this.#forceKillTimeout = setTimeout(() => {
            child.kill("SIGKILL");
        }, APP_SERVER_PROCESS_KILL_GRACE_MS);
        this.#forceKillTimeout.unref?.();
    }
    #clearTimers() {
        if (this.#processTimeout !== null) {
            clearTimeout(this.#processTimeout);
            this.#processTimeout = null;
        }
        if (this.#forceKillTimeout !== null) {
            clearTimeout(this.#forceKillTimeout);
            this.#forceKillTimeout = null;
        }
    }
    emitProviderConversationObserved(threadId) {
        const agentControllerConversationId = normalizeOptionalText(threadId);
        if (!agentControllerConversationId) {
            return;
        }
        this.#emitRuntimeEvent({
            agentControllerConversationId,
            phase: "provider_conversation_observed",
            tsMs: Date.now(),
        });
    }
    #emitRuntimeEvent(event) {
        this.#onRuntimeEvent?.(event);
    }
}
function readThreadIdFromResponse(value) {
    const response = toRecord(value);
    return readRequiredText(response?.thread?.id, "thread response thread.id");
}
function readTurnStartResponse(value) {
    const response = toRecord(value);
    const turn = response?.turn;
    if (!turn || typeof turn !== "object") {
        throw new CodexAppServerTransportError({
            code: "protocol.invalid",
            errorType: "adapter",
            message: "protocol.invalid: turn/start response did not include turn",
        });
    }
    return turn;
}
function readAgentMessageOutput(turn) {
    const items = Array.isArray(turn.items) ? turn.items : [];
    const text = items
        .filter((item) => item.type === "agentMessage")
        .map((item) => normalizeOptionalText(item.text))
        .filter((item) => item !== null)
        .join("\n")
        .trim();
    return text.length > 0 ? text : null;
}
function readTurnFailureMessage(turn) {
    return (normalizeOptionalText(turn.error?.message) ?? "codex app-server turn failed");
}
function readTurnFailureCodexErrorInfo(turn) {
    return normalizeOptionalText(turn.error?.codexErrorInfo);
}
function readAgentMessageDeltaText(value) {
    if (typeof value !== "string" || value.length === 0) {
        return null;
    }
    return value;
}
async function emitPreviewIfRequested(input) {
    if (!(input.onPreview && input.streamingMode === "stream")) {
        return;
    }
    await input.onPreview({
        text: input.output,
    });
}
function buildServerRequestDeclineResponse(method) {
    switch (method) {
        case "item/commandExecution/requestApproval":
            return { decision: "decline" };
        case "item/fileChange/requestApproval":
            return { decision: "decline" };
        case "execCommandApproval":
            return { decision: "denied" };
        case "applyPatchApproval":
            return { decision: "denied" };
        case "item/tool/requestUserInput":
            return { answers: {} };
        case "mcpServer/elicitation/request":
            return { _meta: null, action: "decline", content: null };
        case "item/tool/call":
            return { contentItems: [], success: false };
        case "item/permissions/requestApproval":
            return {
                permissions: {},
                scope: "turn",
                strictAutoReview: true,
            };
        default:
            return null;
    }
}
function toJsonRpcTransportError(error) {
    const message = normalizeOptionalText(error.message) ?? "codex app-server error";
    const permissionDenial = classifyRuntimePermissionDenialMessage({
        message,
        source: "codex",
    });
    if (permissionDenial) {
        return new CodexAppServerTransportError({
            code: permissionDenial.code,
            errorType: permissionDenial.errorType,
            message: permissionDenial.message,
        });
    }
    const providerFailure = classifyCodexProviderAccountFailure({ message });
    if (providerFailure) {
        return new CodexAppServerTransportError({
            code: providerFailure.code,
            errorType: providerFailure.errorType,
            message,
        });
    }
    const lowered = message.toLowerCase();
    if (lowered.includes("not logged in") ||
        lowered.includes("unauthorized") ||
        lowered.includes("authentication") ||
        lowered.includes("401")) {
        return new CodexAppServerTransportError({
            code: "auth.required",
            errorType: "auth",
            message,
        });
    }
    if (lowered.includes("thread not found") ||
        lowered.includes("thread not loaded") ||
        lowered.includes("no thread") ||
        lowered.includes("not found")) {
        return new CodexAppServerTransportError({
            code: "context_not_found",
            errorType: "config",
            message,
        });
    }
    return new CodexAppServerTransportError({
        code: "upstream_unreachable",
        errorType: "adapter",
        message,
    });
}
function toCodexAppServerTransportError(error, timeoutMs) {
    if (error instanceof CodexAppServerTransportError) {
        return error;
    }
    if (error &&
        typeof error === "object" &&
        "code" in error &&
        typeof error.code === "string" &&
        "errorType" in error &&
        typeof error.errorType === "string" &&
        error instanceof Error) {
        return new CodexAppServerTransportError({
            code: error.code,
            errorType: error.errorType,
            message: error.message,
        });
    }
    if (timeoutMs !== null && toErrorMessage(error).includes("timed out")) {
        return new CodexAppServerTransportError({
            code: "process.timeout",
            errorType: "timeout",
            message: toErrorMessage(error),
        });
    }
    return new CodexAppServerTransportError({
        code: "upstream_unreachable",
        errorType: "adapter",
        message: `upstream_unreachable: failed to execute codex app-server (${toErrorMessage(error)})`,
    });
}
function readRequiredText(value, label) {
    const text = normalizeOptionalText(value);
    if (text) {
        return text;
    }
    throw new CodexAppServerTransportError({
        code: "protocol.invalid",
        errorType: "adapter",
        message: `protocol.invalid: missing ${label}`,
    });
}
function toRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }
    return value;
}
function normalizeText(value) {
    return String(value ?? "").trim();
}
function normalizeOptionalText(value) {
    const normalized = normalizeText(value);
    return normalized.length > 0 ? normalized : null;
}
function toErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}
